import { LeafletMap } from './LeafletMap';
import { LeafletSelectAreaFeature } from './LeafletSelectAreaFeature';

function createMapApi(map: LeafletMap, ref: any) {
    // The area selector is created lazily and bound to this map instance,
    // replacing the old single shared window.LeafletSelectAreaFeature that
    // resolved maps through window.ItemsManager.
    let selectAreaFeature: LeafletSelectAreaFeature | null = null;

    function getSelectAreaFeature(): LeafletSelectAreaFeature {
        if (selectAreaFeature === null) {
            selectAreaFeature = new LeafletSelectAreaFeature(map);
        }
        return selectAreaFeature;
    }

    return {
        // The old engine had no destroy for maps; Leaflet's own teardown is the
        // correct deterministic cleanup now that disposal is per instance.
        dispose: () => map?.map?.remove(),

        setMapClickHandler: (context: any) => map?.setClickHandler(context),

        removeMapClickHandler: (context: any) => map?.removeClickHandler(context),

        captureImage: () => map?.captureImage(),

        addPolygonLayer: (layerId: string, features: any[], mapLayer: any) => map?.addPolygonLayer(layerId, features, mapLayer),

        addMarkerLayer: (layerId: string, features: any[], mapLayer: any) => map?.addMarkerLayer(layerId, features, mapLayer),

        addWebMapLayer: (layerId: string, mapLayer: any) => map?.addWebMapLayer(layerId, mapLayer),

        appendToPolygonLayer: (layer: any, features: any[]) => map?.appendToPolygonLayer(layer, features),

        appendToMarkerLayer: (layer: any, features: any[]) => map?.appendToMarkerLayer(layer, features),

        updateFeatureTooltipInMarkerLayer: (id: any, layerId: any, tooltip: any) => map?.updateFeatureTooltipInMarkerLayer(id, layerId, tooltip),

        updateFeatureTooltipInPolygonLayer: (id: any, layerId: any, tooltip: any) => map?.updateFeatureTooltipInPolygonLayer(id, layerId, tooltip),

        closeMarkerTooltip: (id: any, layerId: any) => map?.closeMarkerTooltip(id, layerId),

        closePolygonTooltip: (id: any, layerId: any) => map?.closePolygonTooltip(id, layerId),

        removeFromLayer: (layerId: string, featureIds: string[]) => map?.removeFromLayer(layerId, featureIds),

        removeLayer: (layerId: string) => map?.removeLayer(layerId),

        toggleLayer: (layerId: string, visible: boolean) => map?.toggleLayer(layerId, visible),

        getZoomLevel: () => map?.getZoomLevel() ?? 1000,

        getCenter: () => map?.getCenter(),

        setMouseWheelZoom: (enable: boolean) => map?.setMouseWheelZoom(enable),

        setTooltipOnPolygon: (layerId: string, id: string, tooltip: string, offsetX: number, offsetY: number) => map?.setTooltipOnPolygon(layerId, id, tooltip, offsetX, offsetY),

        closePermanentTooltips: (layerId: string) => map?.closePermanentTooltips(layerId),

        setColorOnPolygons: (layerId: string, color: string, selectedIds: string[]) => map?.setColorOnPolygons(layerId, color, selectedIds),

        setColorsOnPolygons: (layerId: string, colorIds: any[]) => map?.setColorsOnPolygons(layerId, colorIds),

        setColorOnMarkers: (layerId: string, color: string, selectedIds: string[]) => map?.setColorOnMarkers(layerId, color, selectedIds),

        setColorsOnMarkers: (layerId: string, colorIds: any[]) => map?.setColorsOnMarkers(layerId, colorIds),

        setBaseTiles: (context: any) => map?.setBaseTiles(context),

        appendPolygons: (context: any) => map?.appendPolygons(context),

        setColorAndTooltipOnPolygons: (context: any) => map?.setColorAndTooltipOnPolygons(context),

        updateMapSettings: (context: any) => map?.updateMapSettings(context),

        clearLayers: (context: any) => map?.clearLayers(context),

        clearTooltips: () => map?.clearTooltips(),

        appendMarker: (lat: number, lng: number, popup?: string) => map?.appendMarker(lat, lng, popup),

        appendPoints: (context: any) => map?.appendPoints(context),

        appendControl: (context: any) => map?.appendControl(context),

        fitBounds: (context: any): any => map?.fitBounds(context),

        getBounds: () => map?.getBounds(),

        setZoom: (zoomlevel: number) => map?.setZoom(zoomlevel),

        panTo: (context: any) => map?.panTo(context),

        setScale: (scale: number) => map?.setScale(scale),

        resize: () => map?.resize(),

        enableAreaSelector: (context: any) => getSelectAreaFeature().enableAreaSelector(context),

        disableAreaSelector: () => getSelectAreaFeature().disableAreaSelector(),

        getSelectedAreas: (kind: string) => getSelectAreaFeature().getSelectedAreas(kind),

        removeAllAreasDrawn: () => getSelectAreaFeature().removeAllAreasDrawn()
    };
}

export function createMap(ref: any, context: any) {
    let map = new LeafletMap().createMapByRef(ref, context);
    return createMapApi(map, ref);
}
